import React, { createContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

// Context for theme management
export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // RTL cache for emotion (used by MUI)
  const cacheRtl = useMemo(
    () =>
      createCache({
        key: 'muirtl',
        stylisPlugins: [prefixer, rtlPlugin],
      }),
    []
  );

  // Create theme with RTL and Farsi font
  const theme = useMemo(
    () =>
      createTheme({
        direction: 'rtl',
        typography: {
          fontFamily: '"Vazir", "Tahoma", "Arial", sans-serif',
        },
        palette: {
          primary: {
            main: '#0077cc',
            light: '#4da3ff',
            dark: '#005299',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#4caf50',
            light: '#80e27e',
            dark: '#087f23',
            contrastText: '#ffffff',
          },
          error: {
            main: '#f44336',
          },
          warning: {
            main: '#ff9800',
          },
          info: {
            main: '#2196f3',
          },
          success: {
            main: '#4caf50',
          },
          background: {
            default: '#f5f5f5',
            paper: '#ffffff',
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                fontWeight: 'bold',
                borderRadius: 8,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              rounded: {
                borderRadius: 12,
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                },
              },
            },
          },
        },
      }),
    []
  );

  // Theme settings value to be provided
  const themeContextValue = {
    direction: 'rtl',
    theme,
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <CacheProvider value={cacheRtl}>
        <MuiThemeProvider theme={theme}>
          {children}
        </MuiThemeProvider>
      </CacheProvider>
    </ThemeContext.Provider>
  );
};